import { isRunningInNativeApp } from '~/utils/browserInfo';

/**
 * Consentimento de rastreamento vindo do app nativo (ATT no iOS).
 *
 * Exigência da App Store (Guideline 5.1.2(i)): dentro do app iOS, os pixels
 * de marketing (Facebook, GTM, Kwai, Taboola) só podem ser injetados se o
 * usuário autorizar o rastreamento via App Tracking Transparency. O app
 * nativo resolve o ATT e envia o veredito pela ponte (`attStatus`).
 *
 * Default (antes do veredito chegar):
 *   - Fora do app (navegador) → liberado; comportamento web inalterado.
 *   - App Android → liberado; não há ATT e há builds em produção que não
 *     enviam `attStatus` — bloquear regrediria o tracking do Android publicado.
 *   - App iOS (ou WebView ambíguo não-Android) → bloqueado até o nativo
 *     confirmar `granted`. Fail-closed: bridge quebrada = sem tracking.
 */
type Consent = 'pending' | 'allowed' | 'blocked';

const consent = ref<Consent | null>(null);

function computeDefault(): Consent {
  if (typeof window === 'undefined') {
    return 'blocked';
  }
  if (!isRunningInNativeApp()) {
    return 'allowed';
  }

  const ua = navigator.userAgent || '';

  if (/Android/i.test(ua)) {
    return 'allowed';
  }

  return 'pending';
}

export function useNativeTrackingConsent() {
  if (consent.value === null) {
    consent.value = computeDefault();
  }

  const setNativeConsent = (allowed: boolean): void => {
    consent.value = allowed ? 'allowed' : 'blocked';
  };

  const isTrackingAllowed = computed<boolean>(
    () => consent.value === 'allowed',
  );

  return { isTrackingAllowed, setNativeConsent };
}
